import React from "react"
import { useCallback, useMemo, useEffect } from "react"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import {
  pathForRoute,
  routeNameFromPathname,
  ROUTE_STATE_PARAMS,
  ROUTE_URL_ONLY_PARAMS,
  splitRouteParams,
} from "./route-map"
import { setScreenOptions, clearScreenOptions } from "./screen-options-store"
import { openImageGallery } from "~utils/open-image-gallery"
import type { ImageGalleryProps } from "~pages/app-stack/image-gallery/types"

type NavigateFn = {
  navigate: (name: string, params?: Record<string, unknown>) => void
  goBack: () => void
  replace: (name: string, params?: Record<string, unknown>) => void
  canGoBack: () => boolean
  setOptions: (_options: Record<string, unknown>) => void
  addListener: (_event: string, _cb: () => void) => { remove: () => void }
}

const pendingNavigations: Array<{ name: string; params?: Record<string, unknown> }> = []
let navigationReady = false
let navigateImpl: ((name: string, params?: Record<string, unknown>) => void) | null = null

export const navigationRef = {
  isReady: () => navigationReady,
  navigate(name: string, params?: Record<string, unknown>) {
    if (name === "ImageGallery") {
      openImageGallery((params ?? {}) as ImageGalleryProps)
      return
    }
    if (navigateImpl) navigateImpl(name, params)
    else pendingNavigations.push({ name, params })
  },
  goBack() {
    window.history.back()
  },
  canGoBack: () => window.history.length > 1,
  getRootState: () => ({}),
  dispatch: () => {},
}

export function onNavigationReady() {
  navigationReady = true
  while (pendingNavigations.length > 0) {
    const item = pendingNavigations.shift()
    if (item) navigationRef.navigate(item.name, item.params)
  }
}

export function runWhenNavigationReady(callback: () => void) {
  if (navigationReady) callback()
  else pendingNavigations.push({ name: "__callback__" })
}

export default function useNavigation<T = Record<string, unknown>>(): NavigateFn & T {
  const navigate = useNavigate()
  const location = useLocation()

  const navigateByName = useCallback(
    (name: string, params?: Record<string, unknown>) => {
      if (name === "__callback__") return
      if (name === "ImageGallery") {
        openImageGallery((params ?? {}) as ImageGalleryProps)
        return
      }
      const { urlParams, state } = splitRouteParams(name, params)
      const path = pathForRoute(name, urlParams)
      const nextState = Object.keys(state).length > 0 ? state : null
      const samePath = location.pathname === path.split("?")[0]
      navigate(path, {
        state: nextState,
        replace: name === "Chat" && samePath,
      })
    },
    [navigate, location.pathname],
  )

  useMemo(() => {
    navigateImpl = navigateByName
    navigationReady = true
  }, [navigateByName])

  return useMemo(
    () =>
      ({
        navigate: navigateByName,
        push: navigateByName,
        goBack: () => navigate(-1),
        replace: (name: string, params?: Record<string, unknown>) => {
          const { urlParams, state } = splitRouteParams(name, params)
          navigate(pathForRoute(name, urlParams), {
            replace: true,
            state: Object.keys(state).length > 0 ? state : null,
          })
        },
        canGoBack: () => window.history.length > 1,
        setOptions: (options: Record<string, unknown>) => {
          setScreenOptions(location.pathname, options as never)
        },
        addListener: () => ({ remove: () => {} }),
        getState: () => ({ routes: [{ name: location.pathname }] }),
      }) as NavigateFn & T,
    [navigate, navigateByName, location.pathname],
  )
}

export function useRoute<T extends Record<string, unknown> = Record<string, unknown>>() {
  const location = useLocation()
  const params = useParams()
  const [searchParams] = useSearchParams()
  const routeName = routeNameFromPathname(location.pathname)
  const blockedQueryKeys = new Set(ROUTE_STATE_PARAMS[routeName ?? ""] ?? [])

  const query: Record<string, string> = {}
  searchParams.forEach((value, key) => {
    if (!blockedQueryKeys.has(key)) query[key] = value
  })

  const rawStateParams =
    location.state && typeof location.state === "object" && !Array.isArray(location.state)
      ? (location.state as Record<string, unknown>)
      : {}

  const urlOnlyKeys = new Set(ROUTE_URL_ONLY_PARAMS[routeName ?? ""] ?? [])
  const stateParams = { ...rawStateParams }
  urlOnlyKeys.forEach((key) => {
    delete stateParams[key]
  })

  return {
    params: { ...params, ...stateParams, ...query } as T,
    name: location.pathname,
  }
}

/** 为仍通过 props 接收 route/navigation 的 RN 页面注入路由上下文 */
export function LegacyScreenHost({
  component: Component,
}: {
  component: React.ComponentType<{ route?: ReturnType<typeof useRoute>; navigation?: ReturnType<typeof useNavigation> }>
}) {
  const route = useRoute()
  const navigation = useNavigation()
  return <Component route={route} navigation={navigation} />
}

export function navigate(name: string, params?: Record<string, unknown>) {
  navigationRef.navigate(name, params)
}

/** 在 BrowserRouter 内挂载，注册全局 navigate 并在就绪后冲刷 pending */
export function NavigationBridge() {
  useNavigation()
  useEffect(() => {
    onNavigationReady()
  }, [])
  return null
}

export function getActiveRouteName(_state?: unknown) {
  return routeNameFromPathname(window.location.pathname)
}

export function useBackButtonHandler(_canExit: (routeName: string) => boolean) {}

export function useNavigationPersistence(_storage: unknown, _key: string) {
  return {
    initialNavigationState: undefined,
    onNavigationStateChange: () => {},
    isRestored: true,
    prevRouteName: "",
    curRouteName: "",
  }
}

export function canExit(_routeName: string) {
  return false
}

export { Navigate, Outlet, useLocation } from "react-router-dom"
